import { useEffect, useState } from 'react';
import { X, Plus, ListChecks, AlertTriangle, CheckCircle2, NotebookPen, Pencil, ChevronDown, ChevronRight, Clock, Calendar, CalendarDays, Paperclip, MessageSquare } from 'lucide-react';
import { useT } from '../../i18n';
import { fmtShortDate } from '../../utils/date';
import { formatMilestoneDueDate } from '../../components/ProjectForm';
import { ConfirmModal } from '../../components/ConfirmModal';
import { LineTextarea } from '../../components/LineTextarea';
import { todoApi, type TodoItem } from './api';
import { DocumentsList } from '../documents/DocumentsList';
import { TodoComments } from './TodoComments';
import { fmtDocDate } from '../documents/DocumentsList';

// One item: checkbox + label, and an optional precision/description
// underneath — added or edited via the note icon, saved on blur (blank clears
// it). The label is renamed via the pencil icon (Enter/blur saves, Esc
// cancels). Only the checkbox and label toggle the item, not the icons or the
// description. `collapsible` (milestones/tasks view) hides descriptions behind
// a chevron; task screens keep them always visible.
function TodoRow({ todo, taskId, readOnly, collapsible, taskDueDate, run }: {
  todo: TodoItem;
  taskId: number;
  readOnly: boolean;
  collapsible: boolean;
  taskDueDate: string | null;
  run: (p: Promise<unknown>) => void;
}) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(todo.description ?? '');
  const [renaming, setRenaming] = useState(false);
  const [labelDraft, setLabelDraft] = useState(todo.label);
  const [expanded, setExpanded] = useState(!collapsible);
  const [datesOpen, setDatesOpen] = useState(false);
  // [Proposition: documents] The item's documents, folded behind the paperclip.
  const [docsOpen, setDocsOpen] = useState(false);
  const [docCount, setDocCount] = useState(todo.doc_count ?? 0);
  // Earlier versions of the "Description et suivi" (the last entry is the current text).
  const history = todo.description_history ?? [];
  const lastVersion = history.length ? history[history.length - 1] : null;
  const [historyOpen, setHistoryOpen] = useState(false);
  // The item's own comment thread, folded behind the speech bubble.
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(todo.comment_count ?? 0);
  useEffect(() => { setCommentCount(todo.comment_count ?? 0); }, [todo.comment_count]);
  useEffect(() => { setDocCount(todo.doc_count ?? 0); }, [todo.doc_count]);
  const today = new Date();
  const todayYMD = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const overdue = !!todo.due_date && !todo.done && todo.due_date < todayYMD;
  // Dates past the task's due date are saved anyway, with a warning.
  const pastTaskDue = !!taskDueDate && ((!!todo.planned_date && todo.planned_date > taskDueDate) || (!!todo.due_date && todo.due_date > taskDueDate));
  useEffect(() => { if (!editing) setDraft(todo.description ?? ''); }, [todo.description, editing]);
  useEffect(() => { if (!renaming) setLabelDraft(todo.label); }, [todo.label, renaming]);

  const save = () => {
    setEditing(false);
    if (draft.trim() !== (todo.description ?? '')) run(todoApi.update(todo.id, { description: draft }));
  };
  const saveLabel = () => {
    setRenaming(false);
    const label = labelDraft.trim();
    if (label && label !== todo.label) run(todoApi.update(todo.id, { label }));
    else setLabelDraft(todo.label);
  };
  const startDescription = () => { setExpanded(true); setEditing(true); };

  const showDescription = editing || (!!todo.description && expanded);

  return (
    <div className={`vjt-todo-item${todo.done ? ' is-done' : ''}`}>
      <div className="vjt-todo">
        {collapsible && (
          todo.description ? (
            <button type="button" className="vjt-chevron" onClick={() => setExpanded(v => !v)}
              title={expanded ? t('vjt.todo.hideDescription') : t('vjt.todo.showDescription')}>
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : <span className="vjt-chevron-spacer" />
        )}
        {renaming ? (
          <>
            <input type="checkbox" checked={todo.done} disabled />
            <LineTextarea
              className="form-input--bare vjt-todo-rename"
              autoFocus
              value={labelDraft}
              onChange={setLabelDraft}
              onBlur={saveLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); saveLabel(); }
                if (e.key === 'Escape') { setLabelDraft(todo.label); setRenaming(false); }
              }}
            />
          </>
        ) : (
          <label className="vjt-todo-check"
            title={todo.done && todo.done_by ? t('vjt.todo.checkedBy', { who: todo.done_by, date: todo.done_at ? fmtShortDate(todo.done_at.slice(0, 10)) : '' }) : undefined}>
            <input type="checkbox" checked={todo.done} disabled={readOnly}
              onChange={(e) => run(todoApi.update(todo.id, { done: e.target.checked }))} />
            <span className="vjt-todo-label">{todo.label}</span>
          </label>
        )}
        {todo.planned_date && (
          <button type="button" className="vjt-todo-date" title={t('vjt.todo.plannedDate')}
            disabled={readOnly} onClick={() => setDatesOpen(v => !v)}>
            <Clock size={11} /> {formatMilestoneDueDate(todo.planned_date)}
          </button>
        )}
        {todo.due_date && (
          <button type="button" className={`vjt-todo-date${overdue ? ' is-overdue' : ''}`}
            title={overdue ? t('vjt.todo.overdue') : t('vjt.todo.dueDate')}
            disabled={readOnly} onClick={() => setDatesOpen(v => !v)}>
            <Calendar size={11} /> {formatMilestoneDueDate(todo.due_date)}
          </button>
        )}
        {!readOnly && !renaming && (
          <button type="button" className="btn-icon vjt-todo-remove" title={t('vjt.todo.rename')} onClick={() => setRenaming(true)}>
            <Pencil size={12} />
          </button>
        )}
        {!readOnly && !todo.planned_date && !todo.due_date && !datesOpen && (
          <button type="button" className="btn-icon vjt-todo-remove" title={t('vjt.todo.setDates')} onClick={() => setDatesOpen(true)}>
            <CalendarDays size={12} />
          </button>
        )}
        <button type="button" className={`btn-icon vjt-todo-docs${commentCount > 0 ? ' has-docs' : ''}`} title={t('vjt.todo.commentsToggle')} onClick={() => setCommentsOpen(v => !v)}>
          <MessageSquare size={12} />{commentCount > 0 && <span>{commentCount}</span>}
        </button>
        {(!readOnly || docCount > 0) && (
          <button type="button" className={`btn-icon vjt-todo-docs${docCount > 0 ? ' has-docs' : ''}`} title={t('docs.todoToggle')} onClick={() => setDocsOpen(v => !v)}>
            <Paperclip size={12} />{docCount > 0 && <span>{docCount}</span>}
          </button>
        )}
        {!readOnly && !todo.description && !editing && (
          <button type="button" className="btn-icon vjt-todo-remove" title={t('vjt.todo.addDescription')} onClick={startDescription}>
            <NotebookPen size={12} />
          </button>
        )}
        {!readOnly && (
          <button type="button" className="btn-icon vjt-todo-remove" title={t('common.delete')} onClick={() => run(todoApi.remove(todo.id))}>
            <X size={12} />
          </button>
        )}
      </div>
      {pastTaskDue && !todo.done && (
        <div className="vjt-warning vjt-warning--inline">
          <AlertTriangle size={13} /> {t('vjt.todo.pastTaskDue', { date: formatMilestoneDueDate(taskDueDate!) })}
        </div>
      )}
      {commentsOpen && (
        <div className="vjt-todo-docs-panel">
          <TodoComments todoId={todo.id} taskId={taskId} onCount={setCommentCount} />
        </div>
      )}
      {docsOpen && (
        <div className="vjt-todo-docs-panel">
          <DocumentsList target={{ type: 'todo', id: todo.id }} readOnly={readOnly} compact showHeader={false} onChanged={setDocCount} reloadKey={todo} />
        </div>
      )}
      {datesOpen && !readOnly && (
        <div className="vjt-todo-dates-edit">
          <label>
            <span>{t('vjt.todo.plannedDate')}</span>
            <input type="date" className="form-input--bare" value={todo.planned_date ?? ''}
              onChange={(e) => run(todoApi.update(todo.id, { planned_date: e.target.value || null }))} />
          </label>
          <label>
            <span>{t('vjt.todo.dueDate')}</span>
            <input type="date" className="form-input--bare" value={todo.due_date ?? ''}
              onChange={(e) => run(todoApi.update(todo.id, { due_date: e.target.value || null }))} />
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDatesOpen(false)}>{t('common.close')}</button>
        </div>
      )}
      {!showDescription ? null : editing ? (
        <textarea
          className="form-input vjt-todo-desc-input"
          autoFocus
          rows={2}
          value={draft}
          placeholder={t('vjt.todo.descriptionPlaceholder')}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(todo.description ?? ''); setEditing(false); } }}
        />
      ) : todo.description && (
        <div
          className={`vjt-todo-desc${readOnly ? '' : ' is-editable'}`}
          title={readOnly ? undefined : t('vjt.todo.editDescription')}
          onClick={() => { if (!readOnly) setEditing(true); }}
        >
          {todo.description}
        </div>
      )}
      {/* Every earlier version of the description, with who saved it and when (newest first). */}
      {showDescription && !editing && lastVersion && (lastVersion.at || history.length > 1) && (
        <div className="vjt-todo-history">
          {lastVersion.at && (
            <span className="vjt-todo-history-meta">{t('vjt.todo.historyLast', { who: lastVersion.by ?? '—', date: fmtDocDate(lastVersion.at) })}</span>
          )}
          {history.length > 1 && (
            <button type="button" className="vjt-todo-history-toggle" onClick={() => setHistoryOpen(v => !v)}>
              {historyOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />} {t('vjt.todo.historyToggle', { n: history.length - 1 })}
            </button>
          )}
          {historyOpen && history.slice(0, -1).reverse().map((v, i) => (
            <div key={i} className="vjt-todo-history-item">
              <div className="vjt-todo-history-meta">
                {v.at ? t('vjt.todo.historyBy', { who: v.by ?? '—', date: fmtDocDate(v.at) }) : t('vjt.todo.historyInitial')}
              </div>
              <div className="vjt-todo-history-text">{v.text || t('vjt.todo.historyEmpty')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// [Proposition: vue-jalons-taches] A task's to-do list — check, add, remove
// items, each with an optional precision/description. Used in the milestones/tasks view and in every task screen (task
// page, task modal, task info panel). Pass `todos` when the caller already
// has them (the view's tree); otherwise the list loads itself by task id.
// `onChanged` runs after every change so callers can refresh what depends on
// it (whether the task can be marked Done, or its column after a reopen).
// Adding an item to a Done task reopens it, after confirmation — the server
// refuses it otherwise (TASK_DONE).
export function TaskTodoList({ taskId, todos: given, onChanged, onCounts, readOnly = false, showHeader = false, taskDone = false, collapsibleDescriptions = false, taskDueDate = null, onValidateTask }: {
  taskId: number;
  todos?: TodoItem[];
  onChanged?: () => void;
  /** Reports checked / total items whenever the list changes (a card title shows them). */
  onCounts?: (done: number, total: number) => void;
  readOnly?: boolean;
  /** Show a "To-do list (x/y)" label above the list (task screens). */
  showHeader?: boolean;
  /** The task is Done — warns when items are still unchecked. */
  taskDone?: boolean;
  /** Hide item descriptions behind a chevron (milestones/tasks view); task screens show them always. */
  collapsibleDescriptions?: boolean;
  /** The task's own due date ('YYYY-MM-DD') — items dated past it get a warning (still saved). */
  taskDueDate?: string | null;
  /** When given, every item checked on a task that isn't Done offers to validate it (task screens). */
  onValidateTask?: () => void;
}) {
  const { t } = useT();
  const [todos, setTodos] = useState<TodoItem[]>(given ?? []);
  const [draft, setDraft] = useState('');
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);

  useEffect(() => {
    if (given) { setTodos(given); return; }
    todoApi.list(taskId).then(setTodos).catch(() => {});
  // Also reload when the task due date changes: the warnings depend on it.
  }, [taskId, given, taskDueDate]);

  const refresh = async () => {
    if (!given) setTodos(await todoApi.list(taskId));
    onChanged?.();
  };
  const run = (p: Promise<unknown>) => p.then(refresh).catch((e: Error) => alert(e.message));

  const add = () => {
    const label = draft.trim();
    if (!label) return;
    setDraft('');
    todoApi.add(taskId, label)
      .then(refresh)
      .catch((e: Error & { code?: string }) => {
        if (e.code === 'TASK_DONE') setPendingLabel(label);
        else alert(e.message);
      });
  };

  const confirmReopen = () => {
    const label = pendingLabel;
    setPendingLabel(null);
    if (label) run(todoApi.add(taskId, label, true));
  };

  const checked = todos.filter(d => d.done).length;
  const open = todos.length - checked;
  useEffect(() => { onCounts?.(checked, todos.length); }, [checked, todos.length]);

  return (
    <div className="vjt-todos">
      {showHeader && (
        <div className="vjt-todos-header">
          <ListChecks size={13} /> {t('vjt.todo.header')}
          {todos.length > 0 && <span className="vjt-progress-label">{checked}/{todos.length}</span>}
        </div>
      )}
      {onValidateTask && !readOnly && !taskDone && todos.length > 0 && open === 0 && (
        <div className="vjt-prompt">
          <CheckCircle2 size={14} />
          <span>{t('vjt.prompt.task')}</span>
          <button type="button" className="btn btn-accent btn-sm" onClick={onValidateTask}>{t('vjt.prompt.taskAction')}</button>
        </div>
      )}
      {taskDone && open > 0 && (
        <div className="vjt-warning vjt-warning--inline">
          <AlertTriangle size={13} /> {t('vjt.task.doneWithOpenTodos', { n: open })}
        </div>
      )}
      {todos.length === 0 && <div className="vjt-muted">{t('vjt.todo.empty')}</div>}
      {todos.map(todo => (
        <TodoRow key={todo.id} todo={todo} taskId={taskId} readOnly={readOnly} collapsible={collapsibleDescriptions} taskDueDate={taskDueDate ? taskDueDate.slice(0, 10) : null} run={run} />
      ))}
      {!readOnly && (
        <div className="vjt-todo-add">
          <LineTextarea
            value={draft}
            placeholder={t('vjt.todo.addPlaceholder')}
            onChange={setDraft}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
          <button type="button" className="btn btn-ghost btn-sm" disabled={!draft.trim()} onClick={add}>
            <Plus size={12} /> {t('vjt.todo.add')}
          </button>
        </div>
      )}
      <ConfirmModal
        open={pendingLabel != null}
        title={t('vjt.todo.reopenTitle')}
        message={t('vjt.todo.reopenMessage')}
        confirmLabel={t('vjt.todo.reopenConfirm')}
        onConfirm={confirmReopen}
        onCancel={() => setPendingLabel(null)}
      />
    </div>
  );
}
